import React, { useState, useEffect, useRef } from 'react';
import styles from './Compare.module.scss';
import Footer from '../Footer';
import { useSelector } from 'react-redux';
import { getFiGrowthBook } from '../../lib/growthbook';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import FreeTrialCard from '../FreeTrialCard/FreeTrialCard';
import { Redirect, useHistory } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import * as types from '../../types';
import { ReactComponent as NextIcon } from '../../assets/images/next.svg';
import FiMiniImage from '../../assets/images/compare-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/compare-fi-series-three-plus.png';
import FiUltraImage from '../../assets/images/compare-fi-ultra.png';
import classNames from 'classnames';
import { ReactComponent as CloseIcon } from '../../assets/images/icons/close.svg';
import { ReactComponent as TargetIcon } from '../../assets/images/icons/icon_target.svg';
import { ReactComponent as HealthTrendIcon } from '../../assets/images/icons/icon_health_trend.svg';
import { ReactComponent as VetsIcon } from '../../assets/images/icons/icon_vets.svg';
import AttLogo from '../../assets/images/att-logo.jpg';
import VerizonLogo from '../../assets/images/verizon-logo.png';
import TmobileLogo from '../../assets/images/tmobile-logo.png';
import * as events from '../../lib/analytics/events';
import { Button, Group, Stack } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { ReactComponent as DropdownIndicator } from '../../assets/images/icons/chevron_icon.svg';
import useFreeTrialPromoVisible from '../../hooks/useFreeTrialPromoVisible';

enum COMPARE_PRODUCTS {
  FI_MINI,
  FI_MINI_CATS,
  SERIES_3_PLUS,
  FI_ULTRA,
}

enum COMPARE_SECTIONS {
  MAIN,
  GPS_AND_LTE,
  FI_CALLBACK,
  HEALTH,
  VETS,
}

const Compare: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const launchQuiz = useLaunchQuiz();
  const config = useSelector((state: types.AppState) => state.config.siteConfig);
  const compareEnabled = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-pdp', false, config);

  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const productHeadersRef = useRef<HTMLDivElement>(null);
  const gpsSectionRef = useRef<HTMLDivElement>(null);
  const healthSectionRef = useRef<HTMLDivElement>(null);
  const vetsSectionRef = useRef<HTMLDivElement>(null);
  const loggedSectionsRef = useRef({ gps: false, health: false, vets: false });
  const hasScrolledToBottomRef = useRef(false);

  const [isUltraExperienceEnabled] = useState(() => {
    return getFiGrowthBook().getFeatureValue<boolean>('ultra-experience', false);
  });
  const isFreeTrialPromoVisible = useFreeTrialPromoVisible();

  const freeTrialRow = isFreeTrialPromoVisible ? [t('free_trial')] : [];
  // Cat free trial is not currently available on MiniPDP. To restore free trial language for cats,
  // change freeTrialRowCats back to freeTrialRow when cat free trial is enabled in MiniPDP.
  // null (rather than a blank space) renders the grey box with an X to mark the feature as unavailable.
  const freeTrialRowCats = isFreeTrialPromoVisible ? [null] : [];

  // Unlikely that we will add a free trial for ultra, but need to make sure the row is still
  // rendered based on if flag is enabled or not to avoid misalignment in the table.
  // null (rather than a blank space) renders the grey box with an X to mark the feature as unavailable.
  const freeTrialRowUltra = isFreeTrialPromoVisible ? [null] : [];

  const FI_MINI_DETAILS: Record<COMPARE_SECTIONS, (string | React.ReactNode | null)[]> = {
    [COMPARE_SECTIONS.MAIN]: [
      ...freeTrialRow,
      t('attaches_to_collar_harness_more'),
      t('usb_c_charging'),
      <div key="battery-life">
        <div>{t('up_to_2_months_battery_life')}</div>
        <div className={styles.subtitleText}>{t('up_to_3_months_with_fi_base')}</div>
      </div>,
      null,
      t('ideal_for_puppies_small_dogs'),
      t('unlimited_users'),
      t('dimensions_42x30x12_5mm'),
      t('weighs_16_grams'),
      t('waterproof_ip68'),
    ],
    [COMPARE_SECTIONS.GPS_AND_LTE]: [
      <div key="verizon-coverage">
        <div className={styles.verizonLogo}>
          <img src={VerizonLogo} alt="Verizon" />
        </div>
        <div>{t('uses_verizon_cellular_coverage')}</div>
      </div>,
      t('lost_mode_live_tracking'),
      t('unlimited_walk_history'),
      t('24_7_escape_alerts'),
    ],
    [COMPARE_SECTIONS.FI_CALLBACK]: [null],
    [COMPARE_SECTIONS.HEALTH]: [
      t('activity_tracking'),
      t('sleep_tracking'),
      <div key="ai-behavior">
        <div>{t('ai_behavior_detection')}</div>
        <div className={styles.subtitleText}>{t('behavior_tracking_details')}</div>
      </div>,
      t('apple_watch_integration'),
    ],
    [COMPARE_SECTIONS.VETS]: [t('health_report_for_vet'), t('ai_smart_document_organizer')],
  };

  const FI_MINI_CATS_DETAILS: Record<COMPARE_SECTIONS, (string | React.ReactNode | null)[]> = {
    [COMPARE_SECTIONS.MAIN]: [
      ...freeTrialRowCats,
      t('attaches_to_collar_harness_more'),
      t('usb_c_charging'),
      <div key="battery-life-cats">
        <div>{t('up_to_2_months_battery_life')}</div>
        <div className={styles.subtitleText}>{t('up_to_3_months_with_fi_base')}</div>
      </div>,
      null,
      t('ideal_for_cats'),
      t('unlimited_users'),
      t('dimensions_42x30x12_5mm'),
      t('weighs_16_grams'),
      t('waterproof_ip68'),
    ],
    [COMPARE_SECTIONS.GPS_AND_LTE]: [
      <div key="verizon-coverage-cats">
        <div className={styles.verizonLogo}>
          <img src={VerizonLogo} alt="Verizon" />
        </div>
        <div>{t('uses_verizon_cellular_coverage')}</div>
      </div>,
      t('lost_mode_live_tracking'),
      t('unlimited_outside_time_history'),
      t('24_7_escape_alerts'),
    ],
    [COMPARE_SECTIONS.FI_CALLBACK]: [null],
    [COMPARE_SECTIONS.HEALTH]: [t('activity_time_tracking'), t('sleep_tracking'), null, t('apple_watch_integration')],
    [COMPARE_SECTIONS.VETS]: [t('health_report_for_vet'), t('ai_smart_document_organizer')],
  };

  const FI_S3_PLUS_DETAILS: Record<COMPARE_SECTIONS, (string | React.ReactNode | null)[]> = {
    [COMPARE_SECTIONS.MAIN]: [
      ...freeTrialRow,
      t('replaces_dog_collar'),
      t('fi_base_charging'),
      t('up_to_3_months_battery_life'),
      <div key="night-light">
        <div>{t('night_light')}</div>
        <div>{t('pick_your_color')}</div>
      </div>,
      t('designed_for_active_larger_breeds'),
      t('unlimited_users'),
      t('dimensions_81_5x30_3x11_5mm'),
      t('weighs_52_grams'),
      t('waterproof_ip68_66k'),
    ],
    [COMPARE_SECTIONS.GPS_AND_LTE]: [
      <div key="att-coverage">
        <div className={styles.attLogo}>
          <img src={AttLogo} alt="AT&T" width={49} height={16} />
        </div>
        <div>{t('uses_att_cellular_coverage')}</div>
      </div>,
      t('lost_mode_live_tracking'),
      t('unlimited_walk_history'),
      t('24_7_escape_alerts'),
    ],
    [COMPARE_SECTIONS.FI_CALLBACK]: [null],
    [COMPARE_SECTIONS.HEALTH]: [
      t('activity_tracking'),
      t('sleep_tracking'),
      <div key="ai-behavior-s3">
        <div>{t('ai_behavior_detection')}</div>
        <div className={styles.subtitleText}>{t('behavior_tracking_details')}</div>
      </div>,
      t('apple_watch_integration'),
    ],
    [COMPARE_SECTIONS.VETS]: [t('health_report_for_vet'), t('ai_smart_document_organizer')],
  };

  const FI_ULTRA_DETAILS: Record<COMPARE_SECTIONS, (string | React.ReactNode | null)[]> = {
    [COMPARE_SECTIONS.MAIN]: [
      ...freeTrialRowUltra,
      t('attaches_to_collar_or_harness'),
      t('usb_c_charging'),
      <div key="ultra-battery">
        <div>{t('up_to_5_days_battery_life')}</div>
        <div className={styles.subtitleText}>{t('ultra_battery_live_tracking_subtitle')}</div>
      </div>,
      null,
      t('designed_for_adventure_dogs'),
      t('unlimited_users'),
      t('dimensions_75x40x25mm'),
      t('weighs_68_grams'),
      t('waterproof_ip68_66k'),
    ],
    [COMPARE_SECTIONS.GPS_AND_LTE]: [
      <div key="att-coverage">
        <Group position="center" spacing={8} noWrap>
          <img src={AttLogo} alt="AT&T" style={{ height: 16 }} />
          <img src={TmobileLogo} alt="T-Mobile" style={{ height: 14 }} />
        </Group>
        <div>{t('att_tmobile_lte_m_nationwide')}</div>
      </div>,
      t('satellite_connection'),
      t('unlimited_walk_history'),
      t('24_7_escape_alerts'),
    ],
    [COMPARE_SECTIONS.FI_CALLBACK]: [t('sound_and_vibration_recall')],
    // Null used intentionally here to ensure the section still renders
    [COMPARE_SECTIONS.HEALTH]: [null, null, null, null],
    [COMPARE_SECTIONS.VETS]: [null, null],
  };

  const DETAILS_BY_PRODUCT = {
    [COMPARE_PRODUCTS.FI_MINI]: FI_MINI_DETAILS,
    [COMPARE_PRODUCTS.FI_MINI_CATS]: FI_MINI_CATS_DETAILS,
    [COMPARE_PRODUCTS.SERIES_3_PLUS]: FI_S3_PLUS_DETAILS,
    [COMPARE_PRODUCTS.FI_ULTRA]: FI_ULTRA_DETAILS,
  };

  const SECTION_TITLES = {
    [COMPARE_SECTIONS.MAIN]: '',
    [COMPARE_SECTIONS.GPS_AND_LTE]: t('gps_and_lte'),
    [COMPARE_SECTIONS.FI_CALLBACK]: t('fi_callback'),
    [COMPARE_SECTIONS.HEALTH]: t('health'),
    [COMPARE_SECTIONS.VETS]: t('vets'),
  };

  const SECTION_ICONS = {
    [COMPARE_SECTIONS.MAIN]: <></>,
    [COMPARE_SECTIONS.GPS_AND_LTE]: <TargetIcon />,
    [COMPARE_SECTIONS.FI_CALLBACK]: <></>,
    [COMPARE_SECTIONS.HEALTH]: <HealthTrendIcon />,
    [COMPARE_SECTIONS.VETS]: <VetsIcon />,
  };

  // TODO: move this to a media query in the module.scss once migrated to Mantine 7.x+
  const largeScreen = useMediaQuery('(min-width: 1025px)');

  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [activeProducts, setActiveProducts] = useState<COMPARE_PRODUCTS[]>([
    COMPARE_PRODUCTS.FI_MINI,
    COMPARE_PRODUCTS.SERIES_3_PLUS,
  ]);

  const ALL_PRODUCTS: COMPARE_PRODUCTS[] = [
    COMPARE_PRODUCTS.FI_MINI,
    COMPARE_PRODUCTS.SERIES_3_PLUS,
    COMPARE_PRODUCTS.FI_MINI_CATS,
    ...(isUltraExperienceEnabled ? [COMPARE_PRODUCTS.FI_ULTRA] : []),
  ];
  const isActiveOnMobile = (product: COMPARE_PRODUCTS) => activeProducts.includes(product);
  const orderForProduct = (product: COMPARE_PRODUCTS) => {
    const idx = activeProducts.indexOf(product);
    return idx === 0 ? 1 : idx === 1 ? 2 : undefined;
  };

  const PRODUCT_DETAILS: Record<
    COMPARE_PRODUCTS,
    { key: string; title: string; subtitle?: string; imageSrc: string; imageClass: string; route: string }
  > = {
    [COMPARE_PRODUCTS.FI_MINI]: {
      key: 'fi-mini',
      title: t('mini'),
      imageSrc: FiMiniImage,
      imageClass: styles.fiMiniImage,
      route: AppPaths.Products('fi-mini') + '?type=dog',
    },
    [COMPARE_PRODUCTS.SERIES_3_PLUS]: {
      key: 'series-3-plus',
      title: t('series_3_plus'),
      imageSrc: FiSeries3PlusImage,
      imageClass: styles.fiSeries3PlusImage,
      route: AppPaths.Products('smart-collar-v3'),
    },
    [COMPARE_PRODUCTS.FI_MINI_CATS]: {
      key: 'fi-mini-cats',
      title: t('mini'),
      subtitle: t('for_cats'),
      imageSrc: FiMiniImage,
      imageClass: styles.fiMiniImage,
      route: AppPaths.Products('fi-mini') + '?type=cat',
    },
    [COMPARE_PRODUCTS.FI_ULTRA]: {
      key: 'fi-ultra',
      title: t('fi_ultra'),
      imageSrc: FiUltraImage,
      imageClass: styles.fiUltraImage,
      route: AppPaths.Products('fi-ultra'),
    },
  };

  const getProductLabel = (product: COMPARE_PRODUCTS) => {
    const details = PRODUCT_DETAILS[product];
    return details.subtitle ? `${details.title} ${t('for_cats')}` : details.title;
  };

  const getDropdownLabel = (product: COMPARE_PRODUCTS) => {
    if (product === COMPARE_PRODUCTS.FI_MINI_CATS) {
      return t('mini_for_cats');
    }
    return getProductLabel(product);
  };

  const getAvailableOptionsForColumn = () => {
    return ALL_PRODUCTS.map((p) => ({
      value: String(p),
      label: getDropdownLabel(p),
    }));
  };

  const handleSelectChange = (columnIndex: number, value: string) => {
    const newProduct = Number(value) as COMPARE_PRODUCTS;
    setActiveProducts((prev) => {
      const next = [...prev] as COMPARE_PRODUCTS[];
      const otherIndex = columnIndex === 0 ? 1 : 0;

      if (next[otherIndex] === newProduct) {
        // Swap if selecting the product that is already active in the other column
        next[otherIndex] = next[columnIndex];
        next[columnIndex] = newProduct;
      } else {
        next[columnIndex] = newProduct;
      }
      return next;
    });
  };

  useEffect(() => {
    events.comparePageViewed();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!productHeadersRef.current) return;

      const rect = productHeadersRef.current.getBoundingClientRect();
      setShowStickyHeader(rect.bottom < 150);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [compareEnabled]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          if (entry.target === gpsSectionRef.current && !loggedSectionsRef.current.gps) {
            events.comparePageGPSViewed();
            loggedSectionsRef.current.gps = true;
            observer.unobserve(entry.target);
          } else if (entry.target === healthSectionRef.current && !loggedSectionsRef.current.health) {
            events.comparePageHealthViewed();
            loggedSectionsRef.current.health = true;
            observer.unobserve(entry.target);
          } else if (entry.target === vetsSectionRef.current && !loggedSectionsRef.current.vets) {
            events.comparePageVetsViewed();
            loggedSectionsRef.current.vets = true;
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 },
    );

    if (gpsSectionRef.current) observer.observe(gpsSectionRef.current);
    if (healthSectionRef.current) observer.observe(healthSectionRef.current);
    if (vetsSectionRef.current) observer.observe(vetsSectionRef.current);

    return () => observer.disconnect();
  }, []);

  // Track scroll-to-bottom event
  useEffect(() => {
    const handleScroll = () => {
      if (hasScrolledToBottomRef.current) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if user has scrolled to bottom (within 50px threshold)
      if (scrollTop + windowHeight >= documentHeight - 50) {
        events.comparePageScrolledToBottom();
        hasScrolledToBottomRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!compareEnabled) {
    return <Redirect to={AppPaths.NotFound} />;
  }

  const renderComparisonSection = (section: COMPARE_SECTIONS) => {
    const productsToRender = largeScreen ? ALL_PRODUCTS : activeProducts;
    const featuresByProduct = productsToRender.map((p) => DETAILS_BY_PRODUCT[p][section]);
    const maxLength = Math.max(...featuresByProduct.map((f) => f.length));

    const hasAnyContent = featuresByProduct.some((features) => features.some((f) => f));
    if (!hasAnyContent) return null;

    return (
      <div
        key={section}
        className={styles.section}
        ref={
          section === COMPARE_SECTIONS.GPS_AND_LTE
            ? gpsSectionRef
            : section === COMPARE_SECTIONS.HEALTH
              ? healthSectionRef
              : section === COMPARE_SECTIONS.VETS
                ? vetsSectionRef
                : undefined
        }
      >
        {SECTION_TITLES[section] && (
          <div className={styles.sectionTitle}>
            <div className={styles.sectionTitleText}>
              {SECTION_ICONS[section]} {SECTION_TITLES[section]}
            </div>
          </div>
        )}

        <div className={styles.sectionContent}>
          {Array.from({ length: maxLength }, (_, index) => {
            const rowFeatures = featuresByProduct.map((f) => f[index]);
            if (rowFeatures.every((f) => !f)) return null;

            return (
              <div key={index} className={styles.featureRow}>
                {rowFeatures.map((feature, colIdx) => {
                  return (
                    <div
                      key={colIdx}
                      className={classNames(styles.column, {
                        [styles.emptyColumn]: !feature,
                      })}
                    >
                      {feature ? (
                        <span>{feature}</span>
                      ) : (
                        <div className={styles.emptyColumn}>
                          <CloseIcon />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }).filter(Boolean)}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className={classNames(styles.stickyHeader, { [styles.stickyHeaderVisible]: showStickyHeader })}>
        <div className={styles.stickyHeaderContent}>
          <div className={classNames(styles.stickyHeaderItem, styles.productHeaderTitle)}>
            {PRODUCT_DETAILS[activeProducts[0]].title}
            {PRODUCT_DETAILS[activeProducts[0]].subtitle && (
              <span className={styles.productHeaderSubtitle}>{PRODUCT_DETAILS[activeProducts[0]].subtitle}</span>
            )}
          </div>
          <div className={styles.stickyHeaderSeparator}></div>
          <div className={classNames(styles.stickyHeaderItem, styles.productHeaderTitle)}>
            {PRODUCT_DETAILS[activeProducts[1]].title}
            {PRODUCT_DETAILS[activeProducts[1]].subtitle && (
              <span className={styles.productHeaderSubtitle}>{PRODUCT_DETAILS[activeProducts[1]].subtitle}</span>
            )}
          </div>
        </div>
      </div>
      <div className={classNames('compare-page', styles.compareContainer)}>
        <Stack className={styles.header} align="center" spacing={largeScreen ? 20 : 10}>
          <div className={styles.headerTitle}>{t('compare_fi_models')}</div>
          <div className={styles.headerSubtitle}>{t('compare_page_subtitle')}</div>
          <Button
            color="dark"
            component="a"
            href={AppPaths.ProductListingPage}
            radius="xl"
            w={224}
            onClick={() => {
              events.comparePageShopAllClicked();
            }}
          >
            {t('shop_all')}
          </Button>
        </Stack>
        <div className={classNames(styles.compareGrid, { [styles.fourColumns]: isUltraExperienceEnabled })}>
          {!largeScreen && openMenuIndex !== null && (
            <div className={styles.mobileSelectorOverlay} onClick={() => setOpenMenuIndex(null)} />
          )}
          <div className={styles.productHeaders} ref={productHeadersRef}>
            {largeScreen
              ? ALL_PRODUCTS.map((product) => (
                  <div
                    key={PRODUCT_DETAILS[product].key}
                    className={classNames(styles.productHeader, {
                      [styles.hiddenOnMobile]: !isActiveOnMobile(product),
                      [styles.mobileOrderFirst]: orderForProduct(product) === 1,
                      [styles.mobileOrderSecond]: orderForProduct(product) === 2,
                    })}
                  >
                    <div className={styles.productHeaderImage}>
                      <img
                        className={PRODUCT_DETAILS[product].imageClass}
                        src={PRODUCT_DETAILS[product].imageSrc}
                        alt={PRODUCT_DETAILS[product].title}
                      />
                    </div>
                    <div className={styles.productHeaderTitle}>
                      {PRODUCT_DETAILS[product].title}
                      {PRODUCT_DETAILS[product].subtitle && (
                        <span className={styles.productHeaderSubtitle}>{PRODUCT_DETAILS[product].subtitle}</span>
                      )}
                    </div>
                    <div
                      className={styles.buyButton}
                      onClick={() => {
                        events.comparePageBuyItClicked(PRODUCT_DETAILS[product].title);
                        history.push(PRODUCT_DETAILS[product].route);
                      }}
                    >
                      {t('buy_it')}
                      <NextIcon />
                    </div>
                  </div>
                ))
              : activeProducts.map((product, columnIndex) => (
                  <div key={`${PRODUCT_DETAILS[product].key}-${columnIndex}`} className={styles.productHeader}>
                    <div
                      className={classNames(styles.mobileDropdownWrapper, styles.mobileSelector, {
                        [styles.mobileSelectorOpen]: openMenuIndex === columnIndex,
                      })}
                      role="group"
                      aria-label={t('select_product')}
                    >
                      <button
                        type="button"
                        className={classNames(styles.mobileSelectorButton, {
                          [styles.mobileSelectorButtonOpen]: openMenuIndex === columnIndex,
                        })}
                        onClick={() => setOpenMenuIndex((prev) => (prev === columnIndex ? null : columnIndex))}
                        aria-expanded={openMenuIndex === columnIndex}
                        aria-haspopup="listbox"
                      >
                        <span className={styles.mobileSelectorLabel}>
                          {PRODUCT_DETAILS[product].title}
                          {PRODUCT_DETAILS[product].subtitle && (
                            <span className={styles.productHeaderSubtitle}>{PRODUCT_DETAILS[product].subtitle}</span>
                          )}
                        </span>
                        <DropdownIndicator />
                      </button>
                      {openMenuIndex === columnIndex && (
                        <div className={styles.mobileSelectorMenu} role="listbox">
                          {getAvailableOptionsForColumn().map((opt) => (
                            <div
                              key={opt.value}
                              role="option"
                              tabIndex={0}
                              aria-selected={opt.value === String(product)}
                              className={classNames(styles.mobileSelectorMenuItem, {
                                [styles.mobileSelectorMenuItemSelected]: opt.value === String(product),
                              })}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectChange(columnIndex, opt.value);
                                setOpenMenuIndex(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSelectChange(columnIndex, opt.value);
                                  setOpenMenuIndex(null);
                                } else if (e.key === 'Escape') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenMenuIndex(null);
                                }
                              }}
                            >
                              {opt.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={styles.productHeaderImage}>
                      <img
                        className={PRODUCT_DETAILS[product].imageClass}
                        src={PRODUCT_DETAILS[product].imageSrc}
                        alt={PRODUCT_DETAILS[product].title}
                      />
                    </div>
                    <div
                      className={styles.buyButton}
                      onClick={() => {
                        events.comparePageBuyItClicked(PRODUCT_DETAILS[product].title);
                        history.push(PRODUCT_DETAILS[product].route);
                      }}
                    >
                      {t('buy_it')}
                      <NextIcon />
                    </div>
                  </div>
                ))}
          </div>

          <div className={styles.comparison}>
            {[
              COMPARE_SECTIONS.MAIN,
              COMPARE_SECTIONS.GPS_AND_LTE,
              ...(isUltraExperienceEnabled ? [COMPARE_SECTIONS.FI_CALLBACK] : []),
              COMPARE_SECTIONS.HEALTH,
              COMPARE_SECTIONS.VETS,
            ].map((section) => renderComparisonSection(section))}
          </div>
          <div className={styles.compareBox}>
            <div className={styles.compareTitle}>{t('not_sure_which_fi_is_for_you')}</div>
            <div className={styles.compareButtonsContainer}>
              <button
                className={styles.helpMeChooseCta}
                onClick={() => {
                  events.quizQuestionPage.quizButtonTapped();
                  launchQuiz();
                }}
              >
                {t('help_me_choose')}
              </button>
            </div>
          </div>
          {isFreeTrialPromoVisible && (
            <FreeTrialCard
              onStartFreeTrial={() => {
                events.comparePageFreeTrialStartFreeTrial();
                launchQuiz();
              }}
            />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Compare;
